import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useContext, useEffect, useEffectEvent, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import EventMapInner from './EventMapInner';
import { fetchImage } from '../utils';
import { MapContext, ViewOnlyContext, useEventDetails } from '../context';
import { IMAGE_CACHE_NAME } from '../constants';

interface Props {
    mapNumber: number;
}

interface ImageData {
    sourceUrl: string; // The original URL this data was loaded from
    imgUrl: string;
    imgWidth: number;
    imgHeight: number;
    viewWidth: number;
    viewHeight: number;
}

export default function EventMap(props: Props) {
    const backgroundImageRef = useRef(new Image());
    const { eventId } = useContext(MapContext);
    const viewOnly = useContext(ViewOnlyContext);
    const { event, isLoading, error } = useEventDetails(eventId);
    const [imageData, setImageData] = useState<ImageData | null>(null);
    const [isCached, setIsCached] = useState(false);
    const boxRef = useRef<HTMLDivElement>(null);

    const map = event?.maps?.[props.mapNumber];
    const imageUrl = map?.bgImageUrl;

    // Track object URL so we can revoke it when URL changes or component unmounts (avoid leak)
    const objectUrlRef = useRef<string | null>(null);

    // Check if imageData is stale (from a different URL)
    const isImageDataCurrent = imageData?.sourceUrl === imageUrl;

    // Check if the image is cached for offline use
    useEffect(() => {
        const checkCache = async () => {
            if (imageUrl && window.caches) {
                const cache = await window.caches.open(IMAGE_CACHE_NAME);
                const response = await cache.match(imageUrl);
                setIsCached(response !== undefined);
            }
        };
        checkCache();
    }, [imageUrl]);

    // Load the map background image
    useEffect(() => {
        if (!imageUrl) {
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
                objectUrlRef.current = null;
            }
            backgroundImageRef.current.src = '';
            return;
        }

        let cancelled = false;
        fetchImage(imageUrl).then(objectUrl => {
            if (cancelled) {
                URL.revokeObjectURL(objectUrl);
                return;
            }
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
            }
            objectUrlRef.current = objectUrl;
            backgroundImageRef.current.src = objectUrl;
        }, console.error);

        return () => {
            cancelled = true;
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
                objectUrlRef.current = null;
            }
        };
    }, [imageUrl]);

    // Event handler - receives sourceUrl as argument to capture which URL triggered the load
    const onImageLoad = useEffectEvent((sourceUrl: string, ev: Event) => {
        const img = ev.target as HTMLImageElement;
        const view = boxRef.current;

        if (view) {
            setImageData({
                sourceUrl,
                imgUrl: img.src,
                imgWidth: img.width,
                imgHeight: img.height,
                viewWidth: view.clientWidth,
                viewHeight: view.clientHeight,
            });
        }
    });

    // Once the background image is loaded, store the data needed to render the map
    useEffect(() => {
        if (!imageUrl) return;

        const currentImage = backgroundImageRef.current;

        // Pass imageUrl explicitly so it's captured at effect run time
        currentImage.onload = ev => onImageLoad(imageUrl, ev);

        // Cleanup: remove the onload handler if component unmounts or imageUrl changes
        return () => {
            currentImage.onload = null;
        };
    }, [imageUrl]);

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Typography color="error">Error loading event details: {error.message}</Typography>;
    }

    if (!map) {
        const prefix = viewOnly ? '/viewevent' : '/event';
        return (
            <Box
                ref={boxRef}
                sx={{
                    width: '100%',
                    height: '100%',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 3,
                }}
            >
                <Stack spacing={2} alignItems="center" maxWidth={420} textAlign="center">
                    <Typography variant="h6" color="text.primary">
                        No map yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {viewOnly
                            ? 'This event does not have a map.'
                            : 'Add a map image under Properties to place stations and to-dos on a venue plan.'}
                    </Typography>
                    {!viewOnly && (
                        <Button component={RouterLink} to={`${prefix}/${eventId}/settings`} variant="contained">
                            Open Properties
                        </Button>
                    )}
                </Stack>
            </Box>
        );
    }

    return (
        <Box ref={boxRef} sx={{ width: '100%', height: '100%', position: 'relative' }}>
            {isImageDataCurrent && imageData && (
                <EventMapInner
                    key={`${eventId}-${props.mapNumber}-${imageUrl}`}
                    imgUrl={imageData.imgUrl}
                    imgWidth={imageData.imgWidth}
                    imgHeight={imageData.imgHeight}
                    viewWidth={imageData.viewWidth}
                    viewHeight={imageData.viewHeight}
                />
            )}
            {isCached && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 8,
                        right: 8,
                        zIndex: 1000,
                        pointerEvents: 'none',
                    }}
                >
                    <CheckCircleIcon
                        sx={{
                            color: 'success.light',
                            fontSize: 24,
                            filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.3))',
                        }}
                    />
                </Box>
            )}
        </Box>
    );
}
